import { differenceInCalendarDays } from 'date-fns';
import { getIsoDate } from '../../../utils/dateUtils';
import { type TripSearchPayload, type TripSearchResponse, type TripSearchParametersResponse, type AvailabilityBrowseParams, type AvailabilitySearchResponse, type AvailabilityGroupsResponse, type SearchAnalytics, type SearchAnalyticsMetadata, BROWSE_MAX_WINDOW_DAYS } from '../../models/bff/TripSearch';
import type { TripSearchInput, OneWaySearchInput, PassengerSeatingInput } from '../../models/TripSearch';
import type { CreateBookingPayload, BookingResponse, BookingsListResponse, BookingPaymentPayload, BookingPaymentMethod, PaymentAuthenticationNotificationPayload } from '../../models/bff/Booking';
import type { PaymentAuthenticationInput } from '../../models/bff/PaymentAuthentication';
import type { PaymentAuthenticationProviderType } from '../../../models/payment/PaymentProvider';
import type { BookingAvailableAncillariesResponse } from '../../models/bff/Ancillaries';
import type { BookingConfigurationResponse } from '../../models/bff';
import type { PaginationQueryParams } from '../../models/bff/Common';
import { ApiError } from '../../models/ApiError';
import type { AncillarySelectionPayload, QuotationPayload, QuotationResponse } from '../../models/bff/Quotation';
import type { AncillarySelectionInput, QuotationInput } from '../../models/Quotation';
import { BFF_ENDPOINTS, type BffEndpoint } from '../../endpoints/BffEndpoints';
import { BffAuthenticatedApiClient } from '../../clients/BffAuthenticatedApiClient';
import type { HttpResponse } from '../../http/HttpClient';
import { BOOKING_PAYMENT_TIMEOUT_MS } from '../../constants';
import { BaseRepository } from '../BaseRepository';
import { filterQueryParams } from '../../../utils/queryParams';
import type { PassengersListResponse, PassengerEvaluationResponse, PassengerEvaluationPayload, PassengerSeatingPayload } from '../../models/bff/Passenger';
import type { PassengerEvaluationInput } from '../../models/PassengerEvaluation';

const NAME = 'booking';

/** One retry covers a flaky network; a 4xx/5xx just burns one extra call. */
const NOTIFICATION_RETRY_DELAY_MS = 2000;

/**
 * BookingRepository class for handling booking-related API operations.
 * Uses the BFF authenticated API client for trip search operations.
 * Type-safe: Only allows BFF endpoints defined in BFF_ENDPOINTS.
 */
export class BookingRepository extends BaseRepository<BffEndpoint> {
  static override readonly name = NAME;

  constructor() {
    super(BffAuthenticatedApiClient.getInstance());
  }

  /**
   * Retrieve the possible search options (routes) for the user's subscription
   *
   * @param subscriptionId - The subscription ID
   * @returns Promise<HttpResponse<TripSearchParametersResponse>> - The routes allowed in the subscription
   *
   */
  getSearchParameters(subscriptionId: string): Promise<HttpResponse<TripSearchParametersResponse>> {
    return this.apiClient.get<TripSearchParametersResponse>(
      BFF_ENDPOINTS.TRIP_SEARCH_PARAMS,
      {
        pathParams: {
          subscriptionId,
        },
      },
    );
  }

  /**
   * Retrieve the list of possible passengers to include in a booking for this subscription.
   *
   * @param subscriptionId - The subscription ID
   * @returns Promise<HttpResponse<PassengersListResponse>> - The list of passengers available for the subscription
   *
   */
  getPassengers(subscriptionId: string): Promise<HttpResponse<PassengersListResponse>> {
    return this.apiClient.get<PassengersListResponse>(
      BFF_ENDPOINTS.SUBSCRIPTION_PASSENGERS,
      {
        pathParams: {
          subscriptionId,
        },
      },
    );
  }

  /**
   * Evaluate a passenger composition against the subscription's booking rules.
   *
   * @param subscriptionId - The subscription ID
   * @param input - The travel context and the currently selected persons
   * @returns Promise<HttpResponse<PassengerEvaluationResponse>> - Validity, messages, limits and per-person selectability
   */
  evaluatePassengers(subscriptionId: string, input: PassengerEvaluationInput): Promise<HttpResponse<PassengerEvaluationResponse>> {
    return this.apiClient.post<PassengerEvaluationResponse, PassengerEvaluationPayload>(
      BFF_ENDPOINTS.SUBSCRIPTION_PASSENGERS_EVALUATION,
      {
        travel_date: getIsoDate(input.travelDate),
        trip_type: input.tripType,
        passengers: input.passengers.map(this.toPassengerSeatingPayload),
      },
      {
        pathParams: {
          subscriptionId,
        },
      },
    );
  }

  /**
   * Retrieve **cached** availabilities for the user's subscription and search criteria.
   *
   * @param subscriptionId - The subscription ID
   * @param params - The trip search criteria
   * @returns Promise<HttpResponse<AvailabilitySearchResponse>> - The trip search results
   */
  browseAvailability(subscriptionId: string, params: AvailabilityBrowseParams, analytics?: SearchAnalyticsMetadata): Promise<HttpResponse<AvailabilitySearchResponse>> {
    const isValidDateRange = params.from <= params.to && differenceInCalendarDays(params.to, params.from) < BROWSE_MAX_WINDOW_DAYS;
    if (!isValidDateRange) throw new ApiError(`Invalid date range: from=${params.from}, to=${params.to}. The range must be valid and not exceed ${BROWSE_MAX_WINDOW_DAYS} days.`, 400, null);

    return this.apiClient.get<AvailabilitySearchResponse>(
      BFF_ENDPOINTS.TRIP_SEARCH,
      {
        pathParams: { subscriptionId },
        query: params,
        headers: this.buildSearchAnalyticsHeaders(analytics),
      },
    );
  }

  /** RFC 9651 dictionary for the analytics-only `Search-Analytics` header; empty members are left out. */
  private buildSearchAnalyticsHeaders(analytics: Partial<SearchAnalyticsMetadata> = {}): Record<string, string> | undefined {
    const members = {
      passengers: analytics.passengers?.map(({ type, count }) => `${type}-${count}`).join(','),
      tripType: analytics.tripType,
      departureDate: analytics.departureDate,
      returnDate: analytics.returnDate,
      searchId: analytics.searchId,
    };
    const header = Object.entries(members)
      .filter(([, value]) => value)
      .map(([key, value]) => `${key}="${value}"`)
      .join(',');

    return header ? { 'Search-Analytics': header } : undefined;
  }

  /**
   * Get availability parameters grouped by station groups (MAC and custom groups), including
   * per-destination booking windows for GROUP-type destinations. Unlike getSearchParameters,
   * the response is a flat array (not wrapped in a `routes` envelope).
   *
   * @param subscriptionId - The subscription ID
   * @returns Promise<HttpResponse<AvailabilityGroupsResponse>> - The availability groups
   */
  getAvailabilityGroups(subscriptionId: string): Promise<HttpResponse<AvailabilityGroupsResponse>> {
    return this.apiClient.get<AvailabilityGroupsResponse>(
      BFF_ENDPOINTS.AVAILABILITY_GROUPS,
      {
        pathParams: {
          subscriptionId,
        },
      },
    );
  }

  /**
   * Search for available trips for a given wallet and search criteria.
   *
   * @param searchRequest - The trip search criteria
   * @param analytics - Analytics-only metadata sent as the `Search-Analytics` header
   * @returns Promise<HttpResponse<TripSearchResponse>> - The trip search results
   */
  searchTrips(subscriptionId: string, searchParams: TripSearchInput, analytics?: SearchAnalytics): Promise<HttpResponse<TripSearchResponse>> {
    const searchRequest = this.convertSearchParamsToRequest(searchParams);

    return this.apiClient.post<TripSearchResponse>(
      BFF_ENDPOINTS.TRIP_SEARCH,
      searchRequest,
      {
        pathParams: { subscriptionId },
        headers: this.buildSearchAnalyticsHeaders(analytics),
      },
    );
  }

  private convertSearchParamsToRequest(searchParams: TripSearchInput): TripSearchPayload {
    return {
      outbound: this.convertTripParams(searchParams.outbound),
      inbound: searchParams.inbound ? this.convertTripParams(searchParams.inbound) : undefined,
      passengers: searchParams.passengers.map(this.toPassengerSeatingPayload),
    };
  }

  private toPassengerSeatingPayload({ id, seatType }: PassengerSeatingInput): PassengerSeatingPayload {
    return { person_id: id, ...(seatType && { seat_type: seatType }) };
  }

  private convertTripParams(params: OneWaySearchInput): TripSearchPayload['outbound'] {
    return {
      origin: params.origin,
      destination: params.destination,
      date: this.formatDateForApi(params.date),
    };
  }

  /**
   * Create a booking quotation (pre-booking, not yet confirmed) for a given subscription and trip search criteria.
   *
   * @param subscriberId - The subscriber ID
   * @param quotationParams - Quotation parameters
   * @returns Promise<HttpResponse<QuotationResponse>> - The created quote information like pricing and quota usage.
   */
  createQuotation(subscriptionId: string, quotationParams: QuotationInput): Promise<HttpResponse<QuotationResponse>> {
    const quotationRequest = this.convertTripToQuotationRequest(quotationParams);

    return this.apiClient.post<QuotationResponse>(
      BFF_ENDPOINTS.QUOTATION,
      quotationRequest,
      {
        pathParams: { subscriptionId },
      },
    );
  }

  /**
   * List all bookings for a subscriber
   *
   * @param subscriberId - The subscriber ID
   * @param queryParams - Optional query parameters (e.g. page)
   * @returns Promise<HttpResponse<BookingsListResponse>> - The list of bookings
   */
  listBookings(queryParams?: PaginationQueryParams): Promise<HttpResponse<BookingsListResponse>> {
    const filteredParams = filterQueryParams(queryParams);

    return this.apiClient.get<BookingsListResponse>(
      BFF_ENDPOINTS.BOOKING_LIST,
      {
        query: filteredParams,
      },
    );
  }

  /**
   * Get booking details by ID
   *
   * @param subscriptionId - The subscription ID
   * @param bookingId - The booking ID
   * @returns Promise<HttpResponse<BookingResponse>> - The booking details
   */
  getBooking(subscriptionId: string, bookingId: string): Promise<HttpResponse<BookingResponse>> {
    return this.apiClient.get<BookingResponse>(
      BFF_ENDPOINTS.BOOKING,
      {
        pathParams: {
          subscriptionId,
          bookingId,
        },
      },
    );
  }

  /**
   * Get booking details by ID
   *
   * @param subscriptionId - The subscription ID
   * @param quotationId - The quotation ID
   * @returns Promise<HttpResponse<BookingResponse>> - The booking details
   */
  createBooking(subscriptionId: string, quotationId: string): Promise<HttpResponse<BookingResponse>> {
    return this.apiClient.post<BookingResponse, CreateBookingPayload>(
      BFF_ENDPOINTS.CREATE_BOOKING,
      {
        quotation_id: quotationId,
      },
      {
        pathParams: {
          subscriptionId,
        },
      },
    );
  }

  /**
   * Pay a booking: a tokenized card (Spreedly) or an already-captured hosted-page payment (OCTO).
   *
   * @param subscriptionId - The subscription ID
   * @param bookingId - The booking ID
   * @param paymentMethod - Whichever variant of BookingPaymentMethod the provider produced
   * @param paymentAuthentication - 3-D Secure proof; only tenants that authenticate payments send it
   * @returns Promise<HttpResponse<BookingResponse>> - The booking details
   */
  payBooking(subscriptionId: string, bookingId: string, paymentMethod: BookingPaymentMethod, paymentAuthentication?: PaymentAuthenticationInput): Promise<HttpResponse<BookingResponse>> {
    return this.apiClient.post<BookingResponse, BookingPaymentPayload>(
      BFF_ENDPOINTS.BOOKING_PAYMENTS,
      {
        payment_method: paymentMethod,
        // Omitted when absent so tenants without 3DS send payment_method alone.
        ...(paymentAuthentication && { payment_authentication: paymentAuthentication }),
      },
      {
        pathParams: {
          subscriptionId,
          bookingId,
        },
        timeout: BOOKING_PAYMENT_TIMEOUT_MS,
      },
    );
  }

  /**
   * Tell the backend the customer is back from the bank so the workflow reads the verdict.
   * Idempotent from the page's side: a refresh posts it again and gets the same 202.
   *
   * @param subscriptionId - The subscription ID
   * @param bookingId - The booking ID
   * @param provider - Authentication provider of the booking's paymentAuthentication block
   * @returns Promise<HttpResponse<void>> - Resolves on 202 Accepted (no body)
   */
  notifyPaymentAuthentication(subscriptionId: string, bookingId: string, provider: PaymentAuthenticationProviderType): Promise<HttpResponse<void>> {
    return this.apiClient.post<void, PaymentAuthenticationNotificationPayload>(
      BFF_ENDPOINTS.BOOKING_PAYMENT_AUTHENTICATION_NOTIFICATIONS,
      { provider, metadata: {} },
      {
        pathParams: {
          subscriptionId,
          bookingId,
        },
        retry: 1,
        retryDelay: NOTIFICATION_RETRY_DELAY_MS,
      },
    );
  }

  private convertTripToQuotationRequest({ availabilityId, outboundId, inboundId, ancillaries }: QuotationInput): QuotationPayload {
    return {
      availability_id: availabilityId,
      outbound: {
        id: outboundId,
      },
      ...(inboundId && {
        inbound: {
          id: inboundId,
        },
      }),
      ...(ancillaries && { ancillaries: ancillaries.map(this.toAncillaryPayload) }),
   };

  }

  private toAncillaryPayload({ personId, outbound, inbound }: AncillarySelectionInput): AncillarySelectionPayload {
    return { person_id: personId, outbound, inbound };
  }

  /**
   * Retrieve booking configuration flags for the authenticated user.
   *
   * @returns Promise<HttpResponse<BookingConfigurationResponse>> - The booking features
   */
  getFeatures(subscriptionId: string): Promise<HttpResponse<BookingConfigurationResponse>> {
    return this.apiClient.get<BookingConfigurationResponse>(BFF_ENDPOINTS.BOOKING_FEATURES, { pathParams: { subscriptionId } });
  }

  /**
   * Retrieve available ancillaries for a specific quotation.
   *
   * @param subscriptionId - The subscription ID
   * @param quotationId - The quotation ID
   * @returns Promise<HttpResponse<BookingAvailableAncillariesResponse>> - The available ancillaries
   */
  getAncillaries(subscriptionId: string, quotationId: string): Promise<HttpResponse<BookingAvailableAncillariesResponse>> {
    return this.apiClient.get<BookingAvailableAncillariesResponse>(
      BFF_ENDPOINTS.BOOKING_ANCILLARIES,
      {
        pathParams: { subscriptionId },
        query: { quotation_id: quotationId },
      },
    );
  }
}

declare module '../Repositories' {
  export interface Repositories {
    [NAME]: BookingRepository;
  }
}